import { type SetStateAction } from "react";

import { useGroups } from "@/components/groups/useGroups";
import { useDialogs } from "@/components/shell/Dialogs";
import { TopBar, TopBarAction } from "@/components/shell/TopBar";
import { useMenu } from "@/components/shell/Menu";
import { deleteGroup, removeFromGroup, renameGroup } from "@/lib/groups";
import { announceGroupsChanged } from "@/components/groups/useGroups";
import type { CardsUrlState } from "@/lib/navUrl";
import { Cards } from "@/surfaces/cards/Cards";

import { Drill } from "./Drill";
import { GroupIndex } from "./GroupIndex";

/**
 * 混同 — confusion groups (日本-601). Three places, routed on `NavState.groups`:
 *
 * - **the index** (frame A) — every group, and the drill picker;
 * - **a group** (A2) — 単語 itself, scoped to the group's notes: its list, row menu, bulk bar and
 *   edit dock, the phone's note sheet included;
 * - **a drill** (D/E) — the study screen, graded locally and written nowhere.
 */
export function Groups({
  groupId,
  drill,
  filters,
  onFiltersChange,
  onOpenGroup,
  onDrill,
  onOpenNote,
  navEpoch,
  isPhone,
  footer,
}: {
  groupId: string | null;
  drill: string[] | null;
  filters: CardsUrlState;
  onFiltersChange: (
    update: SetStateAction<CardsUrlState>,
    opts?: { replace?: boolean },
  ) => void;
  onOpenGroup: (groupId: string | null, opts?: { replace?: boolean }) => void;
  onDrill: (groupIds: string[] | null, opts?: { replace?: boolean }) => void;
  onOpenNote: (noteId: number) => void;
  navEpoch: number;
  isPhone: boolean;
  footer?: React.ReactNode;
}) {
  const state = useGroups();
  const dialogs = useDialogs();
  const menu = useMenu();

  if (drill) {
    return (
      <Drill
        key={drill.join(",")}
        groupIds={drill}
        isPhone={isPhone}
        // Leaving a drill is not a place, so it replaces — Back from the index must not walk back
        // into a run that has been ended. Review's `review.deck` makes the same asymmetry.
        onEnd={() => onDrill(null, { replace: true })}
        onOpenNote={onOpenNote}
      />
    );
  }

  const group = groupId ? state.groups.find((g) => g.id === groupId) : undefined;

  if (!groupId || (state.loaded && !group)) {
    return (
      <GroupIndex
        groups={state.groups}
        loaded={state.loaded}
        error={state.error}
        isPhone={isPhone}
        footer={footer}
        onOpen={(id) => onOpenGroup(id)}
        onDrill={(ids) => onDrill(ids)}
      />
    );
  }

  if (!group) return <div className="flex-1 p-6 text-secondary text-ink-3">…</div>;

  const rename = async () => {
    const name = await dialogs.prompt({
      title: "グループ名を変更",
      label: "グループ名",
      initial: group.name,
      submitLabel: "変更",
    });
    if (!name?.trim() || name.trim() === group.name) return;
    try {
      await renameGroup(group.id, name.trim());
    } catch (e) {
      await dialogs.confirm({
        title: "名前を変更できませんでした",
        body: e instanceof Error ? e.message : String(e),
        confirmLabel: "OK",
      });
    }
    announceGroupsChanged();
  };

  const remove = async () => {
    const ok = await dialogs.confirm({
      title: `「${group.name}」を削除しますか？`,
      body: "グループだけを削除します。カードとノートはそのまま残ります。",
      confirmLabel: "グループを削除",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteGroup(group.id);
    } catch (e) {
      await dialogs.confirm({
        title: "グループを削除できませんでした",
        body: e instanceof Error ? e.message : String(e),
        confirmLabel: "OK",
      });
      return;
    }
    announceGroupsChanged();
    onOpenGroup(null, { replace: true });
  };

  const topBar = (
    <TopBar
      title="混同"
      subtitle={`› ${group.name} · ${group.noteCount}枚`}
      destinationId="groups"
      phoneActions={
        <>
          <TopBarAction label="このグループをドリル" onClick={() => onDrill([group.id])}>
            <span className="text-[14px] font-semibold text-accent-text">ドリル</span>
          </TopBarAction>
          <TopBarAction
            label="グループの操作"
            onClick={() =>
              menu.openAt({ x: window.innerWidth - 16, y: 48 }, [
                { kind: "item", id: "g-index", label: "混同に戻る", onSelect: () => onOpenGroup(null) },
                { kind: "item", id: "g-rename", label: "名前を変更…", onSelect: () => void rename() },
                { kind: "separator", id: "g-sep" },
                {
                  kind: "item",
                  id: "g-delete",
                  label: "グループを削除…",
                  danger: true,
                  onSelect: () => void remove(),
                },
              ])
            }
          >
            ⋯
          </TopBarAction>
        </>
      }
    >
      <button
        type="button"
        onClick={() => onOpenGroup(null)}
        data-check="group.back"
        className="text-secondary text-ink-3 hover:text-ink"
      >
        グループ一覧
      </button>
      <button
        type="button"
        onClick={() => void rename()}
        data-check="group.rename"
        className="text-secondary text-ink-3 hover:text-ink"
      >
        名前を変更
      </button>
      <button
        type="button"
        onClick={() => onDrill([group.id])}
        disabled={group.noteCount === 0}
        data-check="group.drill"
        className="rounded-[var(--r-2)] bg-accent px-[13px] py-[6px] text-secondary font-semibold text-white disabled:bg-raised disabled:text-ink-off"
      >
        このグループをドリル
      </button>
      <button
        type="button"
        onClick={() => void remove()}
        data-check="group.delete"
        className="rounded-[var(--r-2)] border border-accent-border px-[11px] py-[5px] text-secondary font-semibold text-accent-text"
      >
        グループを削除
      </button>
    </TopBar>
  );

  return (
    <Cards
      // Keyed on the group: a different group is a different list, and nothing about the one before
      // — focus, checked rows, a pending dock restore — belongs to it.
      key={group.id}
      isPhone={isPhone}
      footer={footer}
      filters={filters}
      onFiltersChange={onFiltersChange}
      navEpoch={navEpoch}
      scope={{
        noteIds: group.noteIds,
        topBar,
        onRemove: (noteIds) =>
          void removeFromGroup(group.id, noteIds).finally(announceGroupsChanged),
      }}
    />
  );
}
